import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { MarkdownViewer, Spinner } from '@core/components/ui';
import { useHSOverlay } from '@core/hooks/useHSOverlay';
import { useAppSelector } from '@core/store/hooks';
import { logger } from '@core/utils';
import { selectIsGuest } from '@features/auth';

import {
  useGetDocumentVersionsQuery,
  useLazyGetDownloadUrlQuery,
  useLazyGetMarkdownUrlQuery,
  useLazyGetPreviewUrlQuery,
  useLazyGetVersionDiffQuery,
  useLazyGetVersionPreviewUrlQuery,
  useLazyGetWopiTokenQuery,
  useRestoreVersionMutation,
} from '../api/dataroomApi';
import { CollaboraViewer } from './CollaboraViewer';
import { CsvViewer } from './CsvViewer';
import { DiffViewer } from './DiffViewer';
import { ImageViewer } from './ImageViewer';
import { PdfViewer } from './PdfViewer';
import { TextViewer } from './TextViewer';
import { VersionHistoryPanel } from './VersionHistoryPanel';

const FILE_ICONS = {
  '.pdf': 'mdi:file-pdf-box',
  '.docx': 'mdi:file-word-box',
  '.doc': 'mdi:file-word-box',
  '.pptx': 'mdi:file-powerpoint-box',
  '.ppt': 'mdi:file-powerpoint-box',
  '.xlsx': 'mdi:file-excel-box',
  '.xls': 'mdi:file-excel-box',
  '.png': 'mdi:file-image-box',
  '.jpg': 'mdi:file-image-box',
  '.jpeg': 'mdi:file-image-box',
  '.txt': 'mdi:file-document-outline',
  '.md': 'mdi:language-markdown',
  '.csv': 'mdi:file-delimited-outline',
  '.zip': 'mdi:folder-zip',
};

const OFFICE_TYPES = new Set(['.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.xls']);
const IMAGE_TYPES = new Set(['.png', '.jpg', '.jpeg']);
// Binary blobs stored as-is on the backend: no markdown extracted, no canvas integration.
const BINARY_PASSTHROUGH_TYPES = new Set(['.zip']);

const COLLABORA_URL = import.meta.env.VITE_COLLABORA_URL || `${window.location.protocol}//${window.location.hostname}:9980`;

/**
 * Routes to the appropriate viewer component based on file type.
 */
function PreviewTab({ document, previewUrl, wopiData }) {
  const { t } = useTranslation();
  const fileType = document.file_type;

  if (!previewUrl && !wopiData) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (OFFICE_TYPES.has(fileType) && wopiData) {
    return (
      <CollaboraViewer
        wopiSrc={wopiData.wopi_src}
        accessToken={wopiData.access_token}
        collaboraUrl={COLLABORA_URL}
      />
    );
  }

  if (!previewUrl) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-[var(--color-text-secondary)]">
        <Icon icon="mdi:file-question" className="size-12 mb-2" />
        <p className="text-sm">{t('dataroom.previewModal.previewUnavailable')}</p>
      </div>
    );
  }

  if (fileType === '.pdf') return <PdfViewer url={previewUrl} />;
  if (fileType === '.csv') return <CsvViewer url={previewUrl} />;
  if (fileType === '.txt') return <TextViewer url={previewUrl} />;
  if (IMAGE_TYPES.has(fileType)) return <ImageViewer url={previewUrl} />;

  if (fileType === '.md') {
    return <MarkdownPreview url={previewUrl} />;
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 text-[var(--color-text-secondary)]">
      <Icon icon="mdi:file-question" className="size-12 mb-2" />
      <p className="text-sm">{t('dataroom.previewModal.previewUnavailableFor', { type: fileType })}</p>
    </div>
  );
}

PreviewTab.propTypes = {
  document: PropTypes.object.isRequired,
  previewUrl: PropTypes.string,
  wopiData: PropTypes.shape({
    wopi_src: PropTypes.string,
    access_token: PropTypes.string,
  }),
};

/**
 * Fetches markdown from URL and renders with MarkdownViewer.
 */
function MarkdownPreview({ url }) {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;

    async function fetchContent() {
      try {
        setIsLoading(true);
        const response = await fetch(url);
        if (!response.ok) throw Object.assign(new Error(`Failed to fetch: ${response.status}`), { status: response.status });
        const text = await response.text();
        if (!cancelled) setContent(text);
      } catch (err) {
        logger.error('MarkdownPreview fetch error:', err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchContent();
    return () => { cancelled = true; };
  }, [url]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="bg-[var(--color-danger-subtle)] border border-[var(--color-danger)] text-sm text-[var(--color-danger)] rounded-lg p-4" role="alert">
          {error.status ? t('dataroom.previewModal.fetchFailed', { status: error.status }) : error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 overflow-auto h-full">
      <MarkdownViewer content={content} />
    </div>
  );
}

MarkdownPreview.propTypes = {
  url: PropTypes.string.isRequired,
};

/**
 * AI View tab that shows Docling-processed markdown content.
 */
function AIViewTab({ document, markdownUrl }) {
  const { t } = useTranslation();
  if (document.status === 'processing') {
    return (
      <div className="flex flex-col items-center justify-center p-12">
        <Spinner size="lg" />
        <p className="mt-4 text-sm text-[var(--color-text-secondary)]">{t('dataroom.previewModal.processing')}</p>
      </div>
    );
  }

  if (document.status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="bg-[var(--color-danger-subtle)] border border-[var(--color-danger)] text-sm text-[var(--color-danger)] rounded-lg p-4" role="alert">
          <div className="flex items-center gap-2">
            <Icon icon="mdi:alert-circle" className="size-5 shrink-0" />
            <div>
              <p>{t('dataroom.previewModal.conversionFailed')}</p>
              <p className="text-xs mt-1">{t('dataroom.previewModal.tryPreviewTab')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!markdownUrl) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return <MarkdownPreview url={markdownUrl} />;
}

AIViewTab.propTypes = {
  document: PropTypes.object.isRequired,
  markdownUrl: PropTypes.string,
};

/**
 * Document preview modal with Preview and AI View tabs.
 * Uses Preline UI HSOverlay for animations, escape key, and backdrop click.
 */
export function DocumentPreviewModal({ document, projectUuid, isOpen, onClose, onSendToCanvas }) {
  // Read the selector here rather than take a prop: the modal is rendered
  // from two places and both must gate version restore identically.
  const { t } = useTranslation();
  const isGuest = useAppSelector(selectIsGuest);
  const [activeTab, setActiveTab] = useState('preview');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [markdownUrl, setMarkdownUrl] = useState(null);
  const [wopiData, setWopiData] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [activeVersionUuid, setActiveVersionUuid] = useState(null);

  const [triggerPreviewUrl] = useLazyGetPreviewUrlQuery();
  const [triggerMarkdownUrl] = useLazyGetMarkdownUrlQuery();
  const [triggerDownloadUrl] = useLazyGetDownloadUrlQuery();
  const [triggerWopiToken] = useLazyGetWopiTokenQuery();
  const [triggerVersionPreviewUrl] = useLazyGetVersionPreviewUrlQuery();
  const [triggerVersionDiff] = useLazyGetVersionDiffQuery();
  const [restoreVersion] = useRestoreVersionMutation();

  const { data: versionsData } = useGetDocumentVersionsQuery(
    { projectUuid, documentUuid: document?.uuid },
    { skip: !showVersions || !document?.uuid },
  );

  // Auto-select current version when panel opens
  useEffect(() => {
    if (versionsData?.length && !activeVersionUuid) {
      setActiveVersionUuid(versionsData[0].version_id); // first = highest version_number (current)
    }
  }, [versionsData, activeVersionUuid]);

  const handleVersionPreview = useCallback(async (version) => {
    const { data } = await triggerVersionPreviewUrl({
      projectUuid,
      documentUuid: document.uuid,
      versionId: version.version_id,
    });
    if (data?.url) {
      setPreviewUrl(data.url);
      setWopiData(null);
      setDiffData(null);
      setActiveVersionUuid(version.version_id);
    }
  }, [document, projectUuid, triggerVersionPreviewUrl]);

  const handleVersionDiff = useCallback(async (version) => {
    const { data } = await triggerVersionDiff({
      projectUuid,
      documentUuid: document.uuid,
      versionId: version.version_id,
    });
    if (data) {
      setDiffData(data);
    }
  }, [document, projectUuid, triggerVersionDiff]);

  const handleVersionDownload = useCallback(async (version) => {
    const { data } = await triggerVersionPreviewUrl({
      projectUuid,
      documentUuid: document.uuid,
      versionId: version.version_id,
    });
    if (data?.url) {
      window.open(data.url, '_blank');
    }
  }, [document, projectUuid, triggerVersionPreviewUrl]);

  const handleVersionRestore = useCallback(async (version) => {
    await restoreVersion({
      projectUuid,
      documentUuid: document.uuid,
      versionId: version.version_id,
    });
  }, [document, projectUuid, restoreVersion]);

  const { overlayId, overlayRef } = useHSOverlay(isOpen, onClose, 'hs-doc-preview');

  useEffect(() => {
    if (!isOpen || !document) return;

    setActiveTab('preview');
    setPreviewUrl(null);
    setMarkdownUrl(null);
    setWopiData(null);
    setShowVersions(false);
    setDiffData(null);

    const params = { projectUuid, documentUuid: document.uuid };

    if (!OFFICE_TYPES.has(document.file_type)) {
      triggerPreviewUrl(params).then(({ data }) => {
        if (data?.url) setPreviewUrl(data.url);
      });
    } else {
      triggerWopiToken(params).then(({ data }) => {
        if (data) setWopiData(data);
      });
    }

    if (document.status === 'ready' && !BINARY_PASSTHROUGH_TYPES.has(document.file_type)) {
      triggerMarkdownUrl(params).then(({ data }) => {
        if (data?.url) setMarkdownUrl(data.url);
      });
    }
  }, [isOpen, document, projectUuid, triggerPreviewUrl, triggerMarkdownUrl, triggerWopiToken]);

  const handleOpenFullscreen = useCallback(() => {
    const viewerUrl = `/viewer/${projectUuid}/${document.uuid}?type=${encodeURIComponent(document.file_type)}`;
    window.open(viewerUrl, '_blank');
  }, [document, projectUuid]);

  const handleDownload = useCallback(async () => {
    const { data } = await triggerDownloadUrl({
      projectUuid,
      documentUuid: document.uuid,
    });
    if (data?.url) {
      window.open(data.url, '_blank');
    }
  }, [document, projectUuid, triggerDownloadUrl]);

  const fileIcon = document ? (FILE_ICONS[document.file_type] || 'mdi:file-outline') : 'mdi:file-outline';
  const useWideModal = document && (document.file_type === '.pdf' || OFFICE_TYPES.has(document.file_type));

  return createPortal(
    <div
      id={overlayId}
      ref={overlayRef}
      className="hs-overlay hidden size-full fixed top-0 start-0 z-80 overflow-x-hidden overflow-y-auto pointer-events-none"
      role="dialog"
      tabIndex={-1}
      aria-labelledby={`${overlayId}-label`}
    >
      <div className="hs-overlay-open:mt-7 hs-overlay-open:opacity-100 hs-overlay-open:duration-500 mt-0 opacity-0 ease-out transition-all w-full m-3 mx-auto min-h-[calc(100%-56px)] flex items-center justify-center">
        <div className={`w-full ${useWideModal ? 'max-w-6xl' : 'max-w-4xl'} mx-4 flex flex-col bg-white border border-[var(--color-border)] shadow-sm rounded-lg pointer-events-auto`} style={{ height: '85vh' }}>
          {document && (
            <>
              {/* Header */}
              <div className="flex items-center gap-3 py-3 px-4 border-b border-[var(--color-border)] shrink-0">
                <Icon icon={fileIcon} className="size-6 text-[var(--color-text-secondary)] shrink-0" />
                <h3
                  id={`${overlayId}-label`}
                  className="text-lg font-semibold text-[var(--color-text)] truncate flex-1 min-w-0"
                  title={document.name}
                >
                  {document.name}
                </h3>
                {onSendToCanvas && !BINARY_PASSTHROUGH_TYPES.has(document.file_type) && (
                  <button
                    type="button"
                    onClick={onSendToCanvas}
                    className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] focus:outline-hidden transition-colors"
                    aria-label={t('dataroom.previewModal.sendToCanvas')}
                  >
                    <Icon icon="mdi:dock-right" className="size-4" />
                    {t('dataroom.previewModal.sendToCanvas')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowVersions(prev => !prev)}
                  className="cursor-pointer size-8 inline-flex justify-center items-center rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
                  aria-label={t('dataroom.previewModal.versionHistory')}
                  title={t('dataroom.previewModal.versionHistory')}
                >
                  <Icon icon="mdi:history" className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={handleOpenFullscreen}
                  className="cursor-pointer size-8 inline-flex justify-center items-center rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text)] focus:outline-hidden"
                  aria-label={t('dataroom.previewModal.openInNewTab')}
                  title={t('dataroom.previewModal.openInNewTab')}
                >
                  <Icon icon="mdi:open-in-new" className="size-5" />
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="cursor-pointer size-8 inline-flex justify-center items-center rounded-lg text-[var(--color-text-secondary)] hover:text-teal-600 hover:bg-teal-100 focus:outline-hidden"
                  aria-label={t('dataroom.previewModal.download')}
                >
                  <Icon icon="mdi:download-outline" className="size-5" />
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="cursor-pointer size-8 inline-flex justify-center items-center rounded-full border border-transparent bg-[var(--color-bg-muted)] text-[var(--color-text)] hover:bg-[var(--color-bg-muted)] focus:outline-hidden"
                  aria-label={t('dataroom.previewModal.close')}
                >
                  <Icon icon="mdi:close" className="size-4" />
                </button>
              </div>

              {/* Tabs */}
              <nav className="flex border-b border-[var(--color-border)] px-4 shrink-0" aria-label={t('dataroom.previewModal.tabs')} role="tablist">
                <button
                  type="button"
                  onClick={() => setActiveTab('preview')}
                  className={`cursor-pointer py-3 px-4 inline-flex items-center gap-x-2 border-b-2 text-sm whitespace-nowrap focus:outline-hidden ${
                    activeTab === 'preview'
                      ? 'font-semibold border-[var(--color-accent)] text-[var(--color-accent)]'
                      : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-accent)]'
                  }`}
                  role="tab"
                  aria-selected={activeTab === 'preview'}
                >
                  {t('dataroom.previewModal.preview')}
                </button>
                {!BINARY_PASSTHROUGH_TYPES.has(document.file_type) && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('ai-view')}
                    className={`cursor-pointer py-3 px-4 inline-flex items-center gap-x-2 border-b-2 text-sm whitespace-nowrap focus:outline-hidden ${
                      activeTab === 'ai-view'
                        ? 'font-semibold border-[var(--color-accent)] text-[var(--color-accent)]'
                        : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-accent)]'
                    }`}
                    role="tab"
                    aria-selected={activeTab === 'ai-view'}
                  >
                    {t('dataroom.previewModal.aiView')}
                    {document.status === 'processing' && (
                      <Spinner size="sm" />
                    )}
                  </button>
                )}
              </nav>

              {/* Tab content */}
              <div className="flex flex-1 overflow-hidden">
                <div className={`flex-1 overflow-hidden ${showVersions ? 'border-r border-[var(--color-border)]' : ''}`} role="tabpanel">
                  {diffData ? (
                    <DiffViewer diff={diffData} onBack={() => setDiffData(null)} />
                  ) : activeTab === 'preview' ? (
                    <PreviewTab
                      document={document}
                      previewUrl={previewUrl}
                      wopiData={wopiData}
                    />
                  ) : (
                    <AIViewTab
                      document={document}
                      markdownUrl={markdownUrl}
                    />
                  )}
                </div>
                {showVersions && (
                  <div className="w-72 overflow-y-auto border-l border-[var(--color-border)]">
                    <div className="p-3 border-b border-[var(--color-border)]">
                      <h3 className="text-sm font-medium text-[var(--color-text)]">{t('dataroom.previewModal.versionHistoryTitle')}</h3>
                    </div>
                    <VersionHistoryPanel
                      versions={versionsData || []}
                      activeVersionUuid={activeVersionUuid}
                      canRestore={!isGuest}
                      onPreview={handleVersionPreview}
                      onDiff={handleVersionDiff}
                      onDownload={handleVersionDownload}
                      onRestore={handleVersionRestore}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    globalThis.document.body
  );
}

DocumentPreviewModal.propTypes = {
  document: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    file_type: PropTypes.string.isRequired,
    status: PropTypes.string,
  }),
  projectUuid: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSendToCanvas: PropTypes.func,
};
